import React, { useState, useEffect, useRef } from 'react'
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ChevronUp, PlusCircle } from 'feather-icons-react/build/IconComponents';
import { setToogleHeader } from '../../core/redux/action';
import { useDispatch, useSelector } from 'react-redux';
import AddPurchases from '../../core/modals/purchases/addpurchases';
import ImportPurchases from '../../core/modals/purchases/importpurchases';
import EditPurchases from '../../core/modals/purchases/editpurchases';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';
import { useToast } from '../../Toaster/ToastContext';
import Loader from '../loader/loader';
import { apiClient } from '../../services/apiClient';
import { Table } from 'antd'

const PurchasesList = () => {

    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    const [purchases, setPurchases] = useState([]);
    const [filteredPurchases, setFilteredPurchases] = useState([]);
    const [loading, setLoading] = useState(false);
    const [currentPurchase, setCurrentPurchase] = useState(null);
    const { showToast } = useToast();
    const hasFetched = useRef(false);

    const renderCollapseTooltip = (props) => (
        <Tooltip id="refresh-tooltip" {...props}>
            Collapse
        </Tooltip>
    );

    const fetchPurchases = async () => {

        if (hasFetched.current) return;
        setLoading(true);
        try {
            const response = await apiClient.post('/get-all-purchase', {
                pageIndex: 0,
                pageSize: 50,
                previousPageIndex: 0,
            })
            if (response.status) {
                setPurchases(response.purchases);
                setFilteredPurchases(response.purchases); 
            } else {
                setPurchases([]);
                setFilteredPurchases([]);
            }
        } catch (error) {
            showToast(error.message || 'Failed to fetch purchases', 'error', 'Purchases');
        } finally {
            setLoading(false);
            hasFetched.current = true;
        }
    };

    const resetFetch = () => {
        hasFetched.current = false;
        fetchPurchases();
    };

    useEffect(() => {
        fetchPurchases();
    }, []);

    const handleSearch = (e) => {
        const term = e.target.value.toLowerCase();
        setFilteredPurchases(
            purchases.filter((purchase) =>
                (purchase.supplier_name && typeof purchase.supplier_name === 'string' ? purchase.supplier_name.toLowerCase().includes(term) : false) ||
                (purchase.reference && typeof purchase.reference === 'string' ? purchase.reference.toLowerCase().includes(term) : false)
            )
        );
    };

    const MySwal = withReactContent(Swal);

    const showConfirmationAlert = (purchase) => {
        MySwal.fire({
            title: 'Are you sure?',
            text: 'You won\'t be able to revert this!',
            showCancelButton: true,
            confirmButtonColor: '#00ff00',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#ff0000',
            cancelButtonText: 'Cancel',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    await apiClient.post('/delete-purchase', {
                        id: purchase.id,
                    });
                    showToast('Purchase deleted successfully', 'success', 'Purchase Delete');
                    resetFetch();
                } catch (error) {
                    showToast(error.message || 'Failed to delete purchase', 'error', 'Purchase Delete');
                } finally {
                    setLoading(false);
                }
            } else {
                MySwal.close();
            }
        });
    };

    const showProductReturnAlert = (purchase) => {
        MySwal.fire({
            title: 'Are you sure?',
            html: `You won't be able to revert this!<br>Returning order with reference: <strong>${purchase.reference}</strong>`,
            showCancelButton: true,
            confirmButtonColor: '#00ff00',
            confirmButtonText: 'Yes, continue it!',
            cancelButtonColor: '#ff0000',
            cancelButtonText: 'Cancel',
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    setLoading(true);
                    await apiClient.post('/return-purchase', {
                        id: purchase.id,
                    });
                    showToast('Purchase returned successfully', 'success', 'Purchase Return');
                    resetFetch();
                } catch (error) {
                    showToast(error.message || 'Failed to return purchase', 'error', 'Purchase Return');
                } finally {
                    setLoading(false);
                }
            } else {
                MySwal.close();
            }
        });
    };

    const handleEditPurchase = async (purchase) => {
        try {
            const response = await apiClient.post('/edit-purchase', {
                id: purchase.id,
            })
            if (response.status) {
                setCurrentPurchase(response.data);
            }
        } catch (error) {
            showToast(error.message || 'Failed to fetch purchase details', 'error', 'Purchase Edit');
        }
    };

    const columns = [
        {
            title: "Supplier Name",
            dataIndex: "supplier_name",
            sorter: (a, b) => (a.supplier_name || '').length - (b.supplier_name || '').length,
        },
        {
            title: "Reference",
            dataIndex: "reference",
            sorter: (a, b) => (a.reference || '').length - (b.reference || '').length,
        },
        {
            title: "Date",
            dataIndex: "date",
            sorter: (a, b) => new Date(a.date) - new Date(b.date),
            render: (text) => {
                const date = new Date(text);
                return isNaN(date) ? 'N/A' : date.toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                }).split('/').join('-');
            },
        },
        {
            title: "Status",
            dataIndex: "status",
            sorter: (a, b) => (a.status || '').length - (b.status || '').length,
            render: (text) => {
                const status = text ? text.toLowerCase() : '';
                if (status === 'returned') {
                    return <span className="badge bg-danger">Returned</span>;
                } else if (status === 'pending') {
                    return <span className="badge" style={{ backgroundColor: '#06AED4' }}>Pending</span>;
                } else if (status === 'received' || status === 'recevied') {
                    return <span className="badge bg-success">Received</span>;
                } else if (status === 'ordered') {
                    return <span className="badge bg-warning">Ordered</span>;
                }
                return text || '';
            },
        },
        {
            title: "Total",
            dataIndex: "total",
            sorter: (a, b) => (a.total || 0) - (b.total || 0),
            render: (text) => text || '0.00',
        },
        {
            title: "Paid",
            dataIndex: "paid",
            sorter: (a, b) => (a.paid || 0) - (b.paid || 0),
            render: (text) => text || '0.00',
        },
        {
            title: "Due",
            dataIndex: "due",
            sorter: (a, b) => (a.due || 0) - (b.due || 0),
            render: (text) => text || '0.00',
        },
        {
            title: "Payment Status",
            dataIndex: "payment_status",
            sorter: (a, b) => (a.payment_status || '').length - (b.payment_status || '').length,
            // render: (text) => (
            //     <span
            //         className="badge"
            //         style={{
            //             backgroundColor: text === 'paid' ? '#EBF8F2' : '#FFE5E5',
            //             color: text === 'paid' ? '#49BE96' : '#FF4D4F',
            //         }}
            //     >
            //         {text === 'paid' ? 'Paid' : 'Unpaid'}
            //     </span>
            // ),
        },
        {
            title: "Action",
            dataIndex: "actions",
            key: "actions",
            render: (_, record) => (
                <td className="action-table-data">
                    <div className="edit-delete-action">
                        <Link
                            className="me-2 p-2"
                            data-bs-toggle="modal"
                            data-bs-target="#add-units"
                            onClick={() => handleEditPurchase(record)}
                        >
                            <i data-feather="edit" className="feather-edit" />
                        </Link>
                        <Link
                            className="confirm-text p-2"
                            to="#"
                            onClick={() => showConfirmationAlert(record)}
                        >
                            <i data-feather="trash-2" className="feather-trash-2" />
                        </Link>
                        <OverlayTrigger
                            placement="top"
                            overlay={
                                record.status === "returned" ? (
                                    <Tooltip id="return-tooltip">Already returned</Tooltip>
                                ) : (
                                    <Tooltip id="return-tooltip">Return order</Tooltip>
                                )
                            }
                        >
                            <Link
                                className={`me-2 p-2 m-2 ${record.status === "returned" ? "disabled" : ""}`}
                                to="#"
                                onClick={record.status === "returned" ? undefined : () => showProductReturnAlert(record)}
                            >
                                <i data-feather="refresh-cw" className="feather-refresh-cw" />
                            </Link>
                        </OverlayTrigger>
                    </div>
                </td>
            ),
        },
    ];

    return (
        <div>
            <Loader loading={loading} />
            <div className="page-wrapper">
                <div className="content">
                    <div className="page-header transfer">
                        <div className="add-item d-flex">
                            <div className="page-title">
                                <h4>Purchase List</h4>
                                <h6>Manage your purchases</h6>
                            </div>
                        </div>
                        <ul className="table-top-head">
                            <li>
                                <OverlayTrigger placement="top" overlay={renderCollapseTooltip}>
                                    <Link
                                        data-bs-toggle="tooltip"
                                        data-bs-placement="top"
                                        id="collapse-header"
                                        className={data ? "active" : ""}
                                        onClick={() => { dispatch(setToogleHeader(!data)) }}
                                    >
                                        <ChevronUp />
                                    </Link>
                                </OverlayTrigger>
                            </li>
                        </ul>
                        <div className="d-flex purchase-pg-btn">
                            <div className="page-btn">
                                <Link
                                    to="#"
                                    className="btn btn-added"
                                    data-bs-toggle="modal"
                                    data-bs-target="#add-units"
                                >
                                    <PlusCircle className="me-2"/> 
                                    Add New Purchase
                                </Link>
                            </div>
                            {/* <div className="page-btn import">
                                <Link
                                    to="#"
                                    className="btn btn-added color"
                                    data-bs-toggle="modal"
                                    data-bs-target="#view-notes"
                                >
                                  
                                    <Download  className="me-2"/>
                                    Import Purchase
                                </Link>
                            </div> */}
                        </div>
                    </div>
                    {/* /product list */}
                    <div className="card table-list-card">
                        <div className="card-body">
                            <div className="table-top">
                                <div className="search-set">
                                    <div className="search-input">
                                        <input
                                            type="text"
                                            placeholder="Search"
                                            className="form-control form-control-sm formsearch"
                                            onChange={handleSearch}
                                        />
                                        <Link to className="btn btn-searchset">
                                            <i data-feather="search" className="feather-search" />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                            <div className="table-responsive">
                                <Table
                                    columns={columns}
                                    dataSource={filteredPurchases}
                                    rowKey={(record) => record.id || record.reference}
                                    rowSelection={null}
                                />
                            </div>
                        </div>
                    </div>
                    {/* /product list */}
                </div>
            </div>
        {/* <AddPurchases fetchPurchases={resetFetch} currentPurchase={currentPurchase}/> */}
        <AddPurchases
            currentPurchase={currentPurchase}
            onClose={() => setCurrentPurchase(null)}
            onUpdate={resetFetch}
        />
        <ImportPurchases />
        <EditPurchases />
        </div>
    )
}

export default PurchasesList
